import { FC } from 'react';
import { Alert, Badge, Card, Col, ListGroup, Row } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import {
  getCaresShellBootstrap,
  type ClientShellCompany,
} from '../shell/bootstrap';
import {
  clientWorkspaceSections,
  type ClientWorkspaceSection,
} from './config';

type ClientWorkspacePageProps = {
  sectionId: string;
};

const toneClassMap: Record<ClientWorkspaceSection['tone'], string> = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  info: 'info',
};

const statusToneMap: Record<ClientShellCompany['status'], string> = {
  active: 'success',
  onboarding: 'warning',
  inactive: 'secondary',
};

const ClientWorkspacePage: FC<ClientWorkspacePageProps> = ({ sectionId }) => {
  const { companySlug } = useParams<{ companySlug: string }>();
  const bootstrap = getCaresShellBootstrap();
  const currentSection =
    clientWorkspaceSections.find((section) => section.id === sectionId) ??
    clientWorkspaceSections[0];
  const company = bootstrap.clientShellCompanies.find((entry) => entry.slug === companySlug);

  if (!bootstrap.authenticated || !bootstrap.canAccessAdmin) {
    return (
      <>
        <CaresPageHeader homepage="Clients" activepage="Clients" page="Access Required" />
        <Alert variant="danger" className="custom-card">
          This area is limited to authenticated platform administrators.
          {!bootstrap.authenticated ? (
            <>
              {' '}
              <Link to="/login" className="alert-link">
                Sign in
              </Link>{' '}
              to continue.
            </>
          ) : null}
        </Alert>
      </>
    );
  }

  if (!company) {
    return (
      <>
        <CaresPageHeader homepage="Clients" activepage="Clients" page="Client Not Found" />
        <Alert variant="warning" className="custom-card">
          The requested client slug is not available in the current client shell bootstrap.
        </Alert>
      </>
    );
  }

  return (
    <>
      <CaresPageHeader homepage="Clients" activepage={company.name} page={currentSection.navLabel} homepageHref="/admin/clients" />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="custom-card cares-page-hero">
            <Card.Body className="p-4 p-xxl-5">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Badge bg="" className={`bg-${statusToneMap[company.status]}-transparent text-${statusToneMap[company.status]}`}>
                      {company.status}
                    </Badge>
                    <Badge bg="" className="bg-light text-muted">
                      Client slug: {company.slug}
                    </Badge>
                  </div>
                  <h2 className="mb-2">{currentSection.title}</h2>
                  <p className="text-muted mb-0">{currentSection.description}</p>
                </div>
                <div className="text-xl-end">
                  <p className="fs-12 text-uppercase text-muted mb-2">Sync source</p>
                  <h4 className="mb-1">Clients registry</h4>
                  <p className="text-muted mb-0 fs-13">
                    This workspace is hydrated from the same live client registry used by the admin clients route and updates in-app when records change.
                  </p>
                </div>
              </div>

              <Row className="g-3">
                <Col md={4}>
                  <Card className="custom-card border shadow-none mb-0 h-100">
                    <Card.Body>
                      <p className="text-muted fs-12 text-uppercase mb-2">Selected company</p>
                      <h4 className="mb-1">{company.name}</h4>
                      <p className="text-muted mb-0">{company.slug}</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="custom-card border shadow-none mb-0 h-100">
                    <Card.Body>
                      <p className="text-muted fs-12 text-uppercase mb-2">Status</p>
                      <h4 className="mb-1 text-capitalize">{company.status}</h4>
                      <p className="text-muted mb-0">Derived from live company records.</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="custom-card border shadow-none mb-0 h-100">
                    <Card.Body>
                      <p className="text-muted fs-12 text-uppercase mb-2">Current action location</p>
                      <h4 className="mb-1">{currentSection.actionLocation.label}</h4>
                      <p className="text-muted mb-0">{currentSection.actionLocation.detail}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row className="g-4 mt-0">
            <Col xxl={7}>
              <Card className="custom-card h-100">
                <Card.Header>
                  <div className="card-title">Available Now</div>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {currentSection.availableNow.map((item) => (
                      <ListGroup.Item key={item} className="px-0">
                        <div className="d-flex align-items-start gap-2">
                          <span className={`badge bg-${toneClassMap[currentSection.tone]}-transparent text-${toneClassMap[currentSection.tone]} mt-1`}>
                            <i className="ri-check-line"></i>
                          </span>
                          <span className="text-muted">{item}</span>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col xxl={5}>
              <Card className="custom-card h-100">
                <Card.Header>
                  <div className="card-title">Action Routing</div>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    This client context comes from the same live registry used by the admin clients workspace. As client records change, the shell bootstrap updates and keeps this route aligned to the latest slug.
                  </p>
                  <div className="border rounded-3 p-3 bg-light">
                    <p className="fw-semibold mb-2">Use this route for context, then act here</p>
                    <div className="d-flex flex-column gap-2">
                      <div>
                        <span className={`badge bg-${toneClassMap[currentSection.tone]}-transparent text-${toneClassMap[currentSection.tone]} me-2`}>
                          Live now
                        </span>
                        <span className="fw-medium">{currentSection.actionLocation.label}</span>
                      </div>
                      <div className="text-muted">{currentSection.actionLocation.detail}</div>
                      <div className="text-muted">
                        Working route: <code>{currentSection.actionLocation.route}</code>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-3 p-3 mt-3">
                    <p className="fw-semibold mb-2">Unavailable On This Page</p>
                    <ListGroup variant="flush">
                      {currentSection.unavailableHere.map((item) => (
                        <ListGroup.Item key={item} className="px-0 py-2 border-0">
                          <div className="d-flex align-items-start gap-2">
                            <span className="badge bg-danger-transparent text-danger mt-1">
                              <i className="ri-close-line"></i>
                            </span>
                            <span className="text-muted">{item}</span>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default ClientWorkspacePage;
